import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isGR6_3TypesOfWorkToPerformParkingSectionB,
	isGR6_3TypesOfWorkToPerformParkingSectionC,
	isGR6_3ParkingSectionW,
	isGR6_3ParkingSectionX,
	isGR6_3ParkingSectionY,
	isResidentialLandPermitGR6_3
} from '../__index.utils'

export const STEP4_3FIELDS = {
	blockNumber: 'blockNumber',
	typesOfWorkToPerformParking: 'typesOfWorkToPerformParking',
	modificationsDrivewayEntranceParking: 'modificationsDrivewayEntranceParking',
	parkingDevelopmentOrModificationDetailsParking:
		'parkingDevelopmentOrModificationDetailsParking',
	areOtherModificationsInvolvedParking: 'areOtherModificationsInvolvedParking',
	desiredExpansionLeftParking: 'desiredExpansionLeftParking',
	desiredExpansionRightParking: 'desiredExpansionRightParking',

	existingWidthParking1: 'existingWidthParking1',
	desiredWidthParking1: 'desiredWidthParking1',
	desiredModificationLeftParking1: 'desiredModificationLeftParking1',
	desiredModificationRightParking1: 'desiredModificationRightParking1',
	desiredModificationLeftDistanceParking1:
		'desiredModificationLeftDistanceParking1',
	desiredModificationRightDistanceParking1:
		'desiredModificationRightDistanceParking1',

	existingWidthParking2: 'existingWidthParking2',
	desiredWidthParking2: 'desiredWidthParking2',
	desiredModificationLeftParking2: 'desiredModificationLeftParking2',
	desiredModificationRightParking2: 'desiredModificationRightParking2',
	desiredModificationLeftDistanceParking2:
		'desiredModificationLeftDistanceParking2',
	desiredModificationRightDistanceParking2:
		'desiredModificationRightDistanceParking2',

	existingWidthParking3: 'existingWidthParking3',
	desiredWidthParking3: 'desiredWidthParking3',
	desiredModificationLeftParking3: 'desiredModificationLeftParking3',
	desiredModificationRightParking3: 'desiredModificationRightParking3',
	desiredModificationLeftDistanceParking3:
		'desiredModificationLeftDistanceParking3',
	desiredModificationRightDistanceParking3:
		'desiredModificationRightDistanceParking3',
	workAreaParking: 'workAreaParking',
	// Wall modification or addition
	wallModificationOrAdditionIsAddingOrMovingWallParking:
		'wallModificationOrAdditionIsAddingOrMovingWallParking',
	wallModificationOrAdditionWallHeightParking:
		'wallModificationOrAdditionWallHeightParking',
	wallModificationOrAdditionWallLengthParking:
		'wallModificationOrAdditionWallLengthParking',
	wallModificationOrAdditionExistingMaterialParking:
		'wallModificationOrAdditionExistingMaterialParking',
	wallModificationOrAdditionProjectedMaterialParking:
		'wallModificationOrAdditionProjectedMaterialParking',

	// Retaining wall modification or addition
	retainingWallModificationOrAdditionIsAddingOrMovingWallParking:
		'retainingWallModificationOrAdditionIsAddingOrMovingWallParking',
	retainingWallModificationOrAdditionWallHeightParking:
		'retainingWallModificationOrAdditionWallHeightParking',
	retainingWallModificationOrAdditionWallLengthParking:
		'retainingWallModificationOrAdditionWallLengthParking',
	retainingWallModificationOrAdditionExistingMaterialParking:
		'retainingWallModificationOrAdditionExistingMaterialParking',
	retainingWallModificationOrAdditionProjectedMaterialParking:
		'retainingWallModificationOrAdditionProjectedMaterialParking',

	// Walkway modification or addition
	walkwayModificationOrAdditionWidthParking:
		'walkwayModificationOrAdditionWidthParking',
	walkwayModificationOrAdditionExistingMaterialParking:
		'walkwayModificationOrAdditionExistingMaterialParking',
	walkwayModificationOrAdditionProjectedMaterialParking:
		'walkwayModificationOrAdditionProjectedMaterialParking',

	desiredExpansionExistingWidthParking: 'desiredExpansionExistingWidthParking',
	desiredExpansionTargetWidthParking: 'desiredExpansionTargetWidthParking'
} as const

export type Step4_3Fields = keyof typeof STEP4_3FIELDS

/**
 * useStep4_3State - Custom hook to manage the state for step 4_1 of the GR6 Land Development Work Permit form.
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_1.
 */

// --- 4.3. State HOOK for Step 4.3 ---
// This hook is used to manage the state of Step 4.3 in the GR6
export function useStep4_3State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR6_3 = isResidentialLandPermitGR6_3(answers?.whatToDo)

	const isSectionB = isGR6_3TypesOfWorkToPerformParkingSectionB(
		answers?.typesOfWorkToPerformParking
	)
	const isSectionC = isGR6_3TypesOfWorkToPerformParkingSectionC(
		answers?.typesOfWorkToPerformParking
	)

	const isSectionW = isGR6_3ParkingSectionW(
		answers?.areOtherModificationsInvolvedParking
	)
	const isSectionX = isGR6_3ParkingSectionX(
		answers?.areOtherModificationsInvolvedParking
	)
	const isSectionY = isGR6_3ParkingSectionY(
		answers?.areOtherModificationsInvolvedParking
	)

	return {
		blockNumber: {
			name: 'blockNumber',
			label: pageAssets?.blockNumber,
			labelKey: 'blockNumber',
			value: answers?.blockNumber || 1,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		typesOfWorkToPerformParking: {
			name: 'typesOfWorkToPerformParking',
			label:
				pageAssets?.request_form_residentialRenovationOrRepairPermit_typesOfWork,
			labelKey: 'request_form_residentialRenovationOrRepairPermit_typesOfWork',
			value: answers?.typesOfWorkToPerformParking?.toString() || '',
			required: isGR6_3,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		modificationsDrivewayEntranceParking: {
			name: 'modificationsDrivewayEntranceParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_modificationsDrivewayEntranceParking,
			labelKey:
				'request_form_LandDevelopmentWork_modificationsDrivewayEntranceParking',
			value: answers?.modificationsDrivewayEntranceParking?.toString() || '',
			required: isSectionB || isSectionC,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		parkingDevelopmentOrModificationDetailsParking: {
			name: 'parkingDevelopmentOrModificationDetailsParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_parkingDevelopmentOrModificationDetailsParking,
			labelKey:
				'request_form_LandDevelopmentWork_parkingDevelopmentOrModificationDetailsParking',
			value:
				answers?.parkingDevelopmentOrModificationDetailsParking?.toString() ||
				'',
			required: false,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		existingWidthParking1: {
			name: 'existingWidthParking1',
			label: pageAssets?.request_form_LandDevelopmentWork_currentWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParking',
			value: answers?.existingWidthParking1 || '',
			required: isSectionB || isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredWidthParking1: {
			name: 'desiredWidthParking1',
			label: pageAssets?.request_form_LandDevelopmentWork_desiredWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParking',
			value: answers?.desiredWidthParking1 || '',
			required: isSectionB || isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftParking1: {
			name: 'desiredModificationLeftParking1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftParking',
			value: answers?.desiredModificationLeftParking1 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightParking1: {
			name: 'desiredModificationRightParking1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationRightParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationRightParking',
			value: answers?.desiredModificationRightParking1 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftDistanceParking1: {
			name: 'desiredModificationLeftDistanceParking1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking',
			value: answers?.desiredModificationLeftDistanceParking1 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightDistanceParking1: {
			name: 'desiredModificationRightDistanceParking1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking',
			value: answers?.desiredModificationRightDistanceParking1 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		existingWidthParkingUnit1: {
			name: 'existingWidthParkingUnit1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_currentWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParkingUnit',
			value:
				answers?.existingWidthParkingUnit1?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredWidthParkingUnit1: {
			name: 'desiredWidthParkingUnit1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParkingUnit',
			value:
				answers?.desiredWidthParkingUnit1 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftDistanceParkingUnit1: {
			name: 'desiredModificationLeftDistanceParkingUnit1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParkingUnit,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParkingUnit',
			value:
				answers?.desiredModificationLeftDistanceParkingUnit1 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightDistanceParkingUnit1: {
			name: 'desiredModificationRightDistanceParkingUnit1',
			label:
				pageAssets?.request_form_LandDevelopmentWork_currentWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParkingUnit',
			value:
				answers?.desiredModificationRightDistanceParkingUnit1 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		existingWidthParking2: {
			name: 'existingWidthParking2',
			label: pageAssets?.request_form_LandDevelopmentWork_currentWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParking',
			value: answers?.existingWidthParking2 || '',
			required:
				(answers?.blockNumber == 2 || answers?.blockNumber == 3) &&
				(isSectionB || isSectionC),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredWidthParking2: {
			name: 'desiredWidthParking2',
			label: pageAssets?.request_form_LandDevelopmentWork_desiredWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParking',
			value: answers?.desiredWidthParking2 || '',
			required:
				(answers?.blockNumber == 2 || answers?.blockNumber == 3) &&
				(isSectionB || isSectionC),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftParking2: {
			name: 'desiredModificationLeftParking2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftParking',
			value: answers?.desiredModificationLeftParking2 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightParking2: {
			name: 'desiredModificationRightParking2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationRightParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationRightParking',
			value: answers?.desiredModificationRightParking2 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftDistanceParking2: {
			name: 'desiredModificationLeftDistanceParking2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking',
			value: answers?.desiredModificationLeftDistanceParking2 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightDistanceParking2: {
			name: 'desiredModificationRightDistanceParking2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking',
			value: answers?.desiredModificationRightDistanceParking2 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredWidthParkingUnit2: {
			name: 'desiredWidthParkingUnit2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParkingUnit',
			value:
				answers?.desiredWidthParkingUnit2 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftDistanceParkingUnit2: {
			name: 'desiredModificationLeftDistanceParkingUnit2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParkingUnit,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParkingUnit',
			value:
				answers?.desiredModificationLeftDistanceParkingUnit2 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		existingWidthParkingUnit2: {
			name: 'existingWidthParkingUnit2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_currentWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParkingUnit',
			value:
				answers?.existingWidthParkingUnit2 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: isSectionB || isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightDistanceParkingUnit2: {
			name: 'desiredModificationRightDistanceParkingUnit2',
			label:
				pageAssets?.request_form_LandDevelopmentWork_currentWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParkingUnit',
			value:
				answers?.desiredModificationRightDistanceParkingUnit2 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		existingWidthParking3: {
			name: 'existingWidthParking3',
			label: pageAssets?.request_form_LandDevelopmentWork_currentWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParking',
			value: answers?.existingWidthParking3 || '',
			required: answers?.blockNumber == 3 && (isSectionB || isSectionC),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredWidthParking3: {
			name: 'desiredWidthParking3',
			label: pageAssets?.request_form_LandDevelopmentWork_desiredWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParking',
			value: answers?.desiredWidthParking3 || '',
			required: answers?.blockNumber == 3 && (isSectionB || isSectionC),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftParking3: {
			name: 'desiredModificationLeftParking3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftParking',
			value: answers?.desiredModificationLeftParking3 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightParking3: {
			name: 'desiredModificationRightParking3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationRightParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationRightParking',
			value: answers?.desiredModificationRightParking3 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftDistanceParking3: {
			name: 'desiredModificationLeftDistanceParking3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking',
			value: answers?.desiredModificationLeftDistanceParking3 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightDistanceParking3: {
			name: 'desiredModificationRightDistanceParking3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParking',
			value: answers?.desiredModificationRightDistanceParking3 || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		existingWidthParkingUnit3: {
			name: 'existingWidthParkingUnit3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_currentWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParkingUnit',
			value:
				answers?.existingWidthParkingUnit3 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredWidthParkingUnit3: {
			name: 'desiredWidthParkingUnit3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParkingUnit',
			value:
				answers?.desiredWidthParkingUnit3 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationLeftDistanceParkingUnit3: {
			name: 'desiredModificationLeftDistanceParkingUnit3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_desiredModificationLeftDistanceParkingUnit,
			labelKey:
				'request_form_LandDevelopmentWork_desiredModificationLeftDistanceParkingUnit',
			value:
				answers?.desiredModificationLeftDistanceParkingUnit3 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredModificationRightDistanceParkingUnit3: {
			name: 'desiredModificationRightDistanceParkingUnit3',
			label:
				pageAssets?.request_form_LandDevelopmentWork_currentWidthParkingUnit,
			labelKey: 'request_form_LandDevelopmentWork_currentWidthParkingUnit',
			value:
				answers?.desiredModificationRightDistanceParkingUnit3 ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		areOtherModificationsInvolvedParking: {
			name: 'areOtherModificationsInvolvedParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_areOtherModificationsInvolvedParking,
			labelKey:
				'request_form_LandDevelopmentWork_areOtherModificationsInvolvedParking',
			value: answers?.areOtherModificationsInvolvedParking?.toString() || '',
			required: false,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		workAreaParking: {
			name: 'workAreaParking',
			label: pageAssets?.request_form_landDevWorkPermit_areaOfWork,
			labelKey: 'request_form_landDevWorkPermit_areaOfWork',
			value: answers?.workAreaParking || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		workAreaParkingUnit: {
			name: 'workAreaParkingUnit',
			label: pageAssets?.request_form_width_unit,
			labelKey: 'request_form_width_unit',
			value:
				answers?.workAreaParkingUnit ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		wallModificationOrAdditionIsAddingOrMovingWallParking: {
			name: 'wallModificationOrAdditionIsAddingOrMovingWallParking',
			label:
				pageAssets?.request_form_landDevWorkPermit_isWallAdditionOrRelocationMuret,
			labelKey:
				'request_form_landDevWorkPermit_isWallAdditionOrRelocationMuret',
			value:
				answers?.wallModificationOrAdditionIsAddingOrMovingWallParking?.toString() ||
				'',
			required: isSectionW,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		wallModificationOrAdditionWallHeightParking: {
			name: 'wallModificationOrAdditionWallHeightParking',
			label: pageAssets?.request_form_landDevWorkPermit_heightWallMuret,
			labelKey: 'request_form_landDevWorkPermit_heightWallMuret',
			value: answers?.wallModificationOrAdditionWallHeightParking || '',
			required: isSectionW,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		wallModificationOrAdditionWallHeightParkingUnit: {
			name: 'wallModificationOrAdditionWallHeightParkingUnit',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.wallModificationOrAdditionWallHeightParkingUnit ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		wallModificationOrAdditionWallLengthParking: {
			name: 'wallModificationOrAdditionWallLengthParking',
			label: pageAssets?.request_form_landDevWorkPermit_lengthWallMuret,
			labelKey: 'request_form_landDevWorkPermit_lengthWallMuret',
			value: answers?.wallModificationOrAdditionWallLengthParking || '',
			required: isSectionW,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		wallModificationOrAdditionWallLengthParkingUnit: {
			name: 'wallModificationOrAdditionWallLengthParkingUnit',
			label: pageAssets?.request_form_length_unit,
			labelKey: 'request_form_length_unit',
			value:
				answers?.wallModificationOrAdditionWallLengthParkingUnit ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		wallModificationOrAdditionExistingMaterialParking: {
			name: 'wallModificationOrAdditionExistingMaterialParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_existingMaterialParking,
			labelKey: 'request_form_LandDevelopmentWork_existingMaterialParking',
			value:
				answers?.wallModificationOrAdditionExistingMaterialParking?.toString() ||
				'',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		wallModificationOrAdditionProjectedMaterialParking: {
			name: 'wallModificationOrAdditionProjectedMaterialParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_projectedMaterialParking,
			labelKey: 'request_form_LandDevelopmentWork_projectedMaterialParking',
			value:
				answers?.wallModificationOrAdditionProjectedMaterialParking?.toString() ||
				'',
			required: isSectionW,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		retainingWallModificationOrAdditionIsAddingOrMovingWallParking: {
			name: 'retainingWallModificationOrAdditionIsAddingOrMovingWallParking',
			label:
				pageAssets?.request_form_landDevWorkPermit_isWallAdditionOrRelocationMuret_label,
			labelKey:
				'request_form_landDevWorkPermit_isWallAdditionOrRelocationMuret_label',
			value:
				answers?.retainingWallModificationOrAdditionIsAddingOrMovingWallParking?.toString() ||
				'',
			required: isSectionX,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		retainingWallModificationOrAdditionWallHeightParking: {
			name: 'retainingWallModificationOrAdditionWallHeightParking',
			label: pageAssets?.request_form_landDevWorkPermit_wallHeight,
			labelKey: 'request_form_landDevWorkPermit_wallHeight',
			value:
				answers?.retainingWallModificationOrAdditionWallHeightParking || '',
			required: isSectionX,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		retainingWallModificationOrAdditionWallHeightParkingUnit: {
			name: 'retainingWallModificationOrAdditionWallHeightParkingUnit',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.retainingWallModificationOrAdditionWallHeightParkingUnit ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		retainingWallModificationOrAdditionWallLengthParking: {
			name: 'retainingWallModificationOrAdditionWallLengthParking',
			label: pageAssets?.request_form_landDevWorkPermit_wallLength,
			labelKey: 'request_form_landDevWorkPermit_wallLength',
			value:
				answers?.retainingWallModificationOrAdditionWallLengthParking || '',
			required: isSectionX,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		retainingWallModificationOrAdditionWallLengthParkingUnit: {
			name: 'retainingWallModificationOrAdditionWallLengthParkingUnit',
			label: pageAssets?.request_form_length_unit,
			labelKey: 'request_form_length_unit',
			value:
				answers?.retainingWallModificationOrAdditionWallLengthParkingUnit ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		retainingWallModificationOrAdditionExistingMaterialParking: {
			name: 'retainingWallModificationOrAdditionExistingMaterialParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_existingMaterialParking,
			labelKey: 'request_form_LandDevelopmentWork_existingMaterialParking',
			value:
				answers?.retainingWallModificationOrAdditionExistingMaterialParking?.toString() ||
				'',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		retainingWallModificationOrAdditionProjectedMaterialParking: {
			name: 'retainingWallModificationOrAdditionProjectedMaterialParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_projectedMaterialParking,
			labelKey: 'request_form_LandDevelopmentWork_projectedMaterialParking',
			value:
				answers?.retainingWallModificationOrAdditionProjectedMaterialParking?.toString() ||
				'',
			required: isSectionX,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		walkwayModificationOrAdditionWidthParking: {
			name: 'walkwayModificationOrAdditionWidthParking',
			label: pageAssets?.request_form_landDevWorkPermit_walkwayWidth,
			labelKey: 'request_form_landDevWorkPermit_walkwayWidth',
			value: answers?.walkwayModificationOrAdditionWidthParking || '',
			required: isSectionY,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		walkwayModificationOrAdditionWidthParkingUnit: {
			name: 'walkwayModificationOrAdditionWidthParkingUnit',
			label: pageAssets?.request_form_width_unit,
			labelKey: 'request_form_width_unit',
			value:
				answers?.walkwayModificationOrAdditionWidthParkingUnit ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		walkwayModificationOrAdditionExistingMaterialParking: {
			name: 'walkwayModificationOrAdditionExistingMaterialParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_existingMaterialParking,
			labelKey: 'request_form_LandDevelopmentWork_existingMaterialParking',
			value:
				answers?.walkwayModificationOrAdditionExistingMaterialParking?.toString() ||
				'',
			required: false,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		walkwayModificationOrAdditionProjectedMaterialParking: {
			name: 'walkwayModificationOrAdditionProjectedMaterialParking',
			label:
				pageAssets?.request_form_LandDevelopmentWork_projectedMaterialParking,
			labelKey: 'request_form_LandDevelopmentWork_projectedMaterialParking',
			value:
				answers?.walkwayModificationOrAdditionProjectedMaterialParking?.toString() ||
				'',
			required: isSectionY,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		desiredExpansionExistingWidthParking: {
			name: 'desiredExpansionExistingWidthParking',
			label:
				pageAssets?.request_form_landDevWorkPermit_desiredExpansionExistingWidthParking,
			labelKey:
				'request_form_landDevWorkPermit_desiredExpansionExistingWidthParking',
			value: answers?.desiredExpansionExistingWidthParking || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredExpansionExistingWidthParkingUnit: {
			name: 'desiredExpansionExistingWidthParkingUnit',
			label: pageAssets?.request_form_width_unit,
			labelKey: 'request_form_width_unit',
			value: answers?.desiredExpansionExistingWidthParkingUnit || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		desiredExpansionTargetWidthParking: {
			name: 'desiredExpansionTargetWidthParking',
			label: pageAssets?.request_form_LandDevelopmentWork_desiredWidthParking,
			labelKey: 'request_form_LandDevelopmentWork_desiredWidthParking',
			value: answers?.desiredExpansionTargetWidthParking || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		desiredExpansionTargetWidthParkingUnit: {
			name: 'desiredExpansionTargetWidthParkingUnit',
			label: pageAssets?.request_form_width_unit,
			labelKey: 'request_form_width_unit',
			value: answers?.desiredExpansionTargetWidthParkingUnit || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
